import { fragmentSource, vertexSource } from "./renderer-shaders";
import { parsePackedBatch } from "./packed-batch";
import { rendererCapacity } from "./renderer-capacity";
import { paintNetworkStage } from "./network-interaction";
import { blendBasisColors, decodeColorMapping } from "./color-mapping";

// Use the production activation functions (including prepared Universal mixtures).
const helpers = fragmentSource.slice(0, fragmentSource.indexOf("void main()"));
const signalSource = helpers + `
precision highp sampler2DArray;
uniform sampler2DArray uSignals;
uniform int uCurrent;
` + fragmentSource.slice(fragmentSource.indexOf("void main()"), fragmentSource.indexOf("  float values[")) + `
  ivec2 pixel = ivec2(gl_FragCoord.xy);
  for (int slot=0; slot<6; slot++) if (uInputIndices[slot]==uCurrent) {
    outColor=vec4(inputs[slot],0.0,0.0,1.0); return;
  }
  vec4 node=texelFetch(uNodes,ivec2(uCurrent,0),0);
  float sum=node.y;
  for (int offset=0; offset<${rendererCapacity.maxConnections}; offset++) {
    if (offset>=int(node.w)) break;
    vec4 edge=texelFetch(uConnections,ivec2(int(node.z)+offset,0),0);
    sum+=texelFetch(uSignals,ivec3(pixel,int(edge.x)),0).r*edge.y;
  }
  float value=0.0;
  if(node.x>=0.0) value=activate(int(node.x),sum);
  else {
    vec4 a=texelFetch(uNodes,ivec2(uCurrent,1),0), b=texelFetch(uNodes,ivec2(uCurrent,2),0);
    for(int kind=0;kind<8;kind++) {
      float weight=kind<4?a[kind]:b[kind-4];
      if(weight>0.0) value+=weight*activate(kind,sum);
    }
  }
  outColor=vec4(value,0.0,0.0,1.0);
}`;
const displaySource = `#version 300 es
precision highp float;
precision highp sampler2DArray;
uniform sampler2DArray uSignals;
uniform int uCurrent;
uniform int uMode;
uniform int uCount;
uniform int uOutputs[8];
uniform vec3 uColors[8];
uniform bool uSoftMax;
uniform bool uClamp;
uniform float uScale;
uniform float uSigns[8];
out vec4 outColor;
void main(){
  ivec2 p=ivec2(gl_FragCoord.xy);
  if(uMode==0){outColor=vec4(vec3(clamp(texelFetch(uSignals,ivec3(p,uCurrent),0).r*.5+.5,0.0,1.0)),1.0);return;}
  float scores[8]; float maximum=-32.0,total=0.0;
  for(int i=0;i<8;i++){
    if(i>=uCount)break;
    float v=texelFetch(uSignals,ivec3(p,uOutputs[i]),0).r*uSigns[i];
    if(isnan(v)||isinf(v))v=0.0;
    if(uClamp)v=max(v,0.0);
    scores[i]=uSoftMax?clamp(v,-32.0,32.0):v;
    maximum=max(maximum,scores[i]);
  }
  if(uSoftMax)for(int i=0;i<8;i++){if(i>=uCount)break;scores[i]=exp(scores[i]-maximum);total+=scores[i];}
  vec3 color=vec3(0.0);
  for(int i=0;i<8;i++){
    if(i>=uCount)break;
    float w=uSoftMax?scores[i]/total:(uClamp?scores[i]:clamp(scores[i]*.5+.5,0.0,1.0));
    if(uMode==2||i==uCurrent)color+=w*uColors[i]*uScale;
  }
  outColor=vec4(clamp(color,0.0,1.0),1.0);
}`;
type Options = { size: number; range: number; centerX?: number; centerY?: number; mapping: number; colors: number[][]; softMax: boolean; clampNegative: boolean; scale: number;
  flipY?: boolean; radialEnabled?: boolean; scaleX?: number; scaleY?: number; legacyCombination?: number; rgbBlend?: number;
  environment?: { mode: number; profile: number; seed: number; octaves: number; persistence: number; amplitude: number; scale: number; offsetX: number; offsetY: number; repeatPeriod: number } };
let revision = 0;
let release: (() => void) | undefined;
export function disposeNetworkGpu(): void { revision++; release?.(); release=undefined; }
export async function renderNetworkGpu(payload: unknown, ids: number[], options: Options): Promise<void> {
  disposeNetworkGpu();
  const request=revision, start=performance.now();
  const status=(message:string)=>{
    for (const id of ["network-gpu-status", "network-load-status"]) {
      const el=document.getElementById(id); if(el)el.textContent=message;
    }
  };
  const stage=async(message:string)=>{status(message);await paintNetworkStage();return request===revision;};
  if(!await stage("Preparing WebGL for network previews…"))return;
  const packet=parsePackedBatch(payload).packets[0];
  const canvas=document.createElement("canvas"); canvas.width=canvas.height=options.size;
  const gl=canvas.getContext("webgl2",{antialias:false,preserveDrawingBuffer:true});
  if(!gl||!gl.getExtension("EXT_color_buffer_float")) throw new Error("Network previews require WebGL 2 floating-point render targets.");
  const textures: WebGLTexture[]=[], programs: WebGLProgram[]=[], shaders: WebGLShader[]=[];
  const buffers: WebGLBuffer[]=[], framebuffers: WebGLFramebuffer[]= [];
  release=()=>{
    textures.forEach(t=>gl.deleteTexture(t)); programs.forEach(p=>gl.deleteProgram(p));shaders.forEach(s=>gl.deleteShader(s));buffers.forEach(b=>gl.deleteBuffer(b));framebuffers.forEach(f=>gl.deleteFramebuffer(f));
    gl.getExtension("WEBGL_lose_context")?.loseContext();
  };
  try {
    if(ids.length>gl.getParameter(gl.MAX_ARRAY_TEXTURE_LAYERS))throw new Error("This network exceeds the device's texture-array capacity.");
    const program=(source:string)=>{
      const p=gl.createProgram()!;programs.push(p);
      for(const [type,text] of [[gl.VERTEX_SHADER,vertexSource],[gl.FRAGMENT_SHADER,source]] as const){
        const s=gl.createShader(type)!;shaders.push(s);gl.shaderSource(s,text);gl.compileShader(s);
        if(!gl.getShaderParameter(s,gl.COMPILE_STATUS))throw new Error(gl.getShaderInfoLog(s)??"Network shader compilation failed");gl.attachShader(p,s);
      }
      gl.linkProgram(p);if(!gl.getProgramParameter(p,gl.LINK_STATUS))throw new Error(gl.getProgramInfoLog(p)??"Network shader linking failed");return p;
    };
    if(!await stage("Compiling network signal shaders…"))return;
    // Specialize only provider selection. Shared production equations stay unchanged,
    // while the driver can discard unused noise paths from a Linear/Repeating view.
    const signalVariant=signalSource
      .replace("uniform int uEnvironmentMode;",`const int uEnvironmentMode = ${Math.max(0,Math.min(2,Math.trunc(options.environment?.mode ?? 0)))};`)
      .replace("uniform int uEnvironmentProfile;",`const int uEnvironmentProfile = ${options.environment?.profile===1?1:0};`);
    const signal=program(signalVariant);
    if(!await stage("Compiling network display shaders…"))return;
    const display=program(displaySource);
    if(!await stage("Allocating GPU signal textures and uploading the network…"))return;
    const buffer=gl.createBuffer()!;buffers.push(buffer);gl.bindBuffer(gl.ARRAY_BUFFER,buffer);
    gl.bufferData(gl.ARRAY_BUFFER,new Float32Array([-1,-1,3,-1,-1,3]),gl.STATIC_DRAW);
    const use=(p:WebGLProgram)=>{gl.useProgram(p);const a=gl.getAttribLocation(p,"a_position");gl.enableVertexAttribArray(a);gl.vertexAttribPointer(a,2,gl.FLOAT,false,0,0);};
    const loc=(p:WebGLProgram,n:string)=>gl.getUniformLocation(p,n);
    const texture=(unit:number,target:number)=>{
      const t=gl.createTexture()!;textures.push(t);gl.activeTexture(gl.TEXTURE0+unit);gl.bindTexture(target,t);
      gl.texParameteri(target,gl.TEXTURE_MIN_FILTER,gl.NEAREST);gl.texParameteri(target,gl.TEXTURE_MAG_FILTER,gl.NEAREST);
      gl.texParameteri(target,gl.TEXTURE_WRAP_S,gl.CLAMP_TO_EDGE);gl.texParameteri(target,gl.TEXTURE_WRAP_T,gl.CLAMP_TO_EDGE);return t;
    };
    const count=ids.length, nodes=new Float32Array(count*12);
    nodes.set(Array.from(packet.nodes));
    if(packet.activationWeights)for(let n=0;n<count;n++)for(let k=0;k<8;k++)nodes[(k<4?count*4:count*8)+n*4+k%4]=packet.activationWeights[n*8+k];
    texture(0,gl.TEXTURE_2D);gl.texImage2D(gl.TEXTURE_2D,0,gl.RGBA32F,count,3,0,gl.RGBA,gl.FLOAT,nodes);
    texture(1,gl.TEXTURE_2D);gl.texImage2D(gl.TEXTURE_2D,0,gl.RGBA32F,Math.max(1,packet.connections.length/4),1,0,gl.RGBA,gl.FLOAT,packet.connections.length?new Float32Array(Array.from(packet.connections)):new Float32Array(4));
    texture(2,gl.TEXTURE_2D_ARRAY);gl.texStorage3D(gl.TEXTURE_2D_ARRAY,1,gl.RGBA32F,options.size,options.size,count);
    const scratch=texture(3,gl.TEXTURE_2D);gl.texStorage2D(gl.TEXTURE_2D,1,gl.RGBA32F,options.size,options.size);
    const f=gl.createFramebuffer()!;framebuffers.push(f);gl.bindFramebuffer(gl.FRAMEBUFFER,f);
    gl.framebufferTexture2D(gl.FRAMEBUFFER,gl.COLOR_ATTACHMENT0,gl.TEXTURE_2D,scratch,0);
    if(gl.checkFramebufferStatus(gl.FRAMEBUFFER)!==gl.FRAMEBUFFER_COMPLETE)throw new Error("Floating-point network framebuffer is incomplete.");
    gl.viewport(0,0,options.size,options.size);use(signal);
    gl.uniform1i(loc(signal,"uNodes"),0);gl.uniform1i(loc(signal,"uConnections"),1);gl.uniform1i(loc(signal,"uSignals"),2);
    gl.uniform2f(loc(signal,"uResolution"),options.size,options.size);gl.uniform1f(loc(signal,"uSpatialRange"),options.range);
    gl.uniform2f(loc(signal,"uSpatialCenter"),options.centerX ?? 0,options.centerY ?? 0);
    gl.uniform2f(loc(signal,"uPhysicalTileSize"),options.size,options.size);
    gl.uniform2f(loc(signal,"uBlockSize"),1,1);gl.uniform1i(loc(signal,"uSourcePass"),1);
    gl.uniform2f(loc(signal,"uSpatialScale"),options.scaleX ?? 1,options.scaleY ?? 1);
    gl.uniform1i(loc(signal,"uFlipY"),(options.flipY ?? true)?1:0);
    gl.uniform1i(loc(signal,"uRadialEnabled"),(options.radialEnabled ?? true)?1:0);
    gl.uniform1i(loc(signal,"uInputMappingKind"),options.mapping);
    const env=options.environment;
    gl.uniform1i(loc(signal,"uEnvironmentMode"),env?.mode ?? 0);gl.uniform1i(loc(signal,"uEnvironmentProfile"),env?.profile ?? 0);
    gl.uniform1i(loc(signal,"uEnvironmentSeed"),env?.seed ?? 0);gl.uniform1i(loc(signal,"uNoiseOctaves"),env?.octaves ?? 4);
    gl.uniform1f(loc(signal,"uNoisePersistence"),env?.persistence ?? .5);gl.uniform1f(loc(signal,"uNoiseAmplitude"),env?.amplitude ?? 1);
    gl.uniform1f(loc(signal,"uNoiseScale"),env?.scale ?? 1);gl.uniform2f(loc(signal,"uNoiseOffset"),env?.offsetX ?? 0,env?.offsetY ?? 0);
    gl.uniform1f(loc(signal,"uRepeatPeriod"),env?.repeatPeriod ?? 8);
    gl.uniform1iv(loc(signal,"uInputIndices[0]"),new Int32Array(Array.from(packet.inputIndices)));
    if(!await stage(`Computing GPU signals · 0/${count} nodes…`))return;
    for(let n=0;n<count;n++){
      if(request!==revision)return;
      use(signal);gl.bindFramebuffer(gl.FRAMEBUFFER,f);gl.uniform1i(loc(signal,"uCurrent"),n);gl.drawArrays(gl.TRIANGLES,0,3);
      // Read array -> separate scratch target -> GPU copy into array. No feedback loop or CPU readback.
      gl.activeTexture(gl.TEXTURE2);gl.bindTexture(gl.TEXTURE_2D_ARRAY,textures[2]);
      gl.copyTexSubImage3D(gl.TEXTURE_2D_ARRAY,0,0,0,n,0,0,options.size,options.size);
      if(n%16===15){status(`Computing GPU signals · ${n+1}/${count} nodes…`);await new Promise<void>(resolve=>requestAnimationFrame(()=>resolve()));}
    }
    if(request!==revision)return;
    if(!await stage("Drawing node previews and color contributions…"))return;
    use(display);gl.bindFramebuffer(gl.FRAMEBUFFER,null);
    const colors=options.colors.map(c=>[...c]), signs=new Float32Array(8).fill(1);
    if(options.legacyCombination !== undefined && options.legacyCombination !== null){
      const mapping=decodeColorMapping(options.legacyCombination), basis=blendBasisColors(options.rgbBlend ?? .2);
      colors.length=3;
      mapping.permutation.forEach((source,channel)=>{colors[source]=basis[channel];signs[source]=(mapping.inversionMask&(1<<channel))?-1:1;});
    }
    gl.uniform1i(loc(display,"uSignals"),2);gl.uniform1i(loc(display,"uCount"),colors.length);
    gl.uniform1fv(loc(display,"uSigns[0]"),signs);
    gl.uniform1iv(loc(display,"uOutputs[0]"),new Int32Array(Array.from(packet.outputIndices)));
    gl.uniform3fv(loc(display,"uColors[0]"),new Float32Array(colors.flat()));
    gl.uniform1i(loc(display,"uSoftMax"),options.softMax?1:0);gl.uniform1i(loc(display,"uClamp"),options.clampNegative?1:0);gl.uniform1f(loc(display,"uScale"),options.scale);
    const draw=(id:string,mode:number,index:number)=>{
      const target=document.getElementById(id) as HTMLCanvasElement|null;if(!target)return;
      gl.uniform1i(loc(display,"uMode"),mode);gl.uniform1i(loc(display,"uCurrent"),index);gl.drawArrays(gl.TRIANGLES,0,3);
      target.getContext("2d")?.drawImage(canvas,0,0);
    };
    ids.forEach((id,n)=>draw(`network-signal-${id}`,0,n));
    Array.from(packet.outputIndices).forEach((n,i)=>draw(`network-contribution-${ids[n]}`,1,i));
    draw("network-final",2,0);
    if(gl.isContextLost()||gl.getError()!==gl.NO_ERROR)throw new Error("GPU network rendering failed; refresh diagnostics to retry.");
    status(`WebGL 2 · ${count} signals · ${(performance.now()-start).toFixed(0)} ms preparation and submission`);
  } catch(error){if(request===revision){status(String(error));disposeNetworkGpu();}throw error;}
}
Object.assign(window,{networkGpu:{render:renderNetworkGpu,dispose:disposeNetworkGpu}});
